import { Injectable, effect, inject, untracked } from '@angular/core';
import { ActivityTrackerService } from './activity-tracker.service';
import { ImpersonationService } from './impersonation.service';
import { DevUser } from '../../features/admin/dev-user.service';

/**
 * Records when Beacon staff start and stop acting as another user ("View as").
 *
 * Watched centrally rather than at each button because "View as" begins and
 * ends in several places — the user list, the dev picker, the topbar's clear —
 * and all of them just swap the DevUser.
 *
 * The first state seen once a real user is known is the baseline and is not
 * logged: a reload mid-impersonation is not a new impersonation, and on a
 * fresh tab the JWT identity arrives after the DevUser is restored. Signing
 * out resets the baseline; an impersonation still open at sign-out is not
 * logged as ended, because there is no session left to attribute it to.
 *
 * Kept apart from ImpersonationService because ActivityTrackerService itself
 * depends on that service.
 */
@Injectable({ providedIn: 'root' })
export class ImpersonationAuditService {
  private readonly impersonation = inject(ImpersonationService);
  private readonly activity = inject(ActivityTrackerService);

  /** undefined = no baseline yet. */
  private last: DevUser | null | undefined = undefined;

  constructor() {
    effect(() => {
      const real = this.impersonation.realUserId();
      const viewed = this.impersonation.viewedUser();
      untracked(() => this.onChange(real, viewed));
    });
  }

  private onChange(realUserId: number | null, viewed: DevUser | null): void {
    if (realUserId == null) {
      this.last = undefined;
      return;
    }
    const prev = this.last;
    this.last = viewed;
    if (prev === undefined || prev?.id === viewed?.id) return;

    if (prev) this.activity.track('security.impersonation_ended', { record: userRef(prev) });
    if (viewed) {
      this.activity.track('security.impersonation_started', {
        record: userRef(viewed),
        details: { viewedCompanyId: viewed.user_company_id, viewedCompany: viewed.company_name || undefined },
      });
    }
  }
}

function userRef(u: DevUser) {
  const name = `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.username || null;
  return { type: 'user', id: u.id, name };
}
